import { useState } from 'react';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

type UseMeasureElementProps = {
    disabled?: boolean;
    keepValuesWhenHidden?: boolean;
};

export type UseMeasureRect = Pick<
    DOMRectReadOnly,
    'x' | 'y' | 'top' | 'left' | 'right' | 'bottom' | 'height' | 'width'
>;
export type UseMeasureRef<E extends Element = Element> = (element: E | null) => void;
export type UseMeasureResult<E extends Element = Element> = [UseMeasureRef<E>, UseMeasureRect];

const defaultState: UseMeasureRect = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
};

const useMeasureElementHook = <E extends HTMLElement = HTMLElement>({
    disabled,
    keepValuesWhenHidden
}: UseMeasureElementProps = {}): UseMeasureResult<E> => {
    const [element, ref] = useState<E | null>(null);

    const [size, setSize] = useState(defaultState);

    useIsomorphicLayoutEffect(() => {
        if (!element || disabled) return undefined;

        const handler = (el: HTMLElement) => {
            setSize((prevSize) => {
                const { x, y, width, height, top, left, bottom, right } =
                    el.getBoundingClientRect();
                if (keepValuesWhenHidden && height === 0 && width === 0) {
                    return prevSize;
                }
                return { x, y, width, height, top, left, bottom, right };
            });
        };

        const resizeObserver = new ResizeObserver(([el]) =>
            el ? handler(el.target as HTMLElement) : null
        );
        resizeObserver.observe(element);

        // call the handler to set initial size
        handler(element);

        return () => resizeObserver.disconnect();
    }, [element, disabled, keepValuesWhenHidden]);

    return [ref, size] as const;
};

export const useMeasureElement =
    typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined'
        ? useMeasureElementHook
        : ((() => [() => null, defaultState]) as typeof useMeasureElementHook);
